import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import {
  getSupervisors,
  addSupervisor,
  removeSupervisor,
} from 'services/adminNegotiationService';
import { getUsersPaged } from 'services/usersServices';
import { adminNegotiationI18n } from './i18n';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import AsyncStateView from '../../components/AsyncStateView/AsyncStateView';
import '../../styles/naEeppTableGrid.scss';

/**
 * Página de Supervisores (Negociación Administrativa). Lista gestionable de usuarios
 * existentes que reciben los avisos por email en los 2 gates del flujo (datos listos /
 * documentos listos). Tabla (nombre/email/estado) + agregar usuario (ComboBox con la
 * búsqueda de usuarios existente, mismo patrón que AdminNegotiationStartCaseModal) +
 * quitar (IconButton con ConfirmModal).
 *
 * Solo design system vía prop DesignSystemComponents + ConfirmModal local.
 */
export default function AdminNegotiationSupervisors({
  DesignSystemComponents,
}) {
  const { t } = useScopedTranslation(adminNegotiationI18n);
  const { Table, Badge, Card, Button, IconButton, ComboBox, Modal, Skeleton } =
    DesignSystemComponents ?? {};

  const [rows, setRows] = useState([]);
  const [supPage, setSupPage] = useState(0);
  const [supPageSize, setSupPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Alta de un supervisor (picker de usuarios).
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [userSearchText, setUserSearchText] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // option {label, value}
  // Estado async del alta (idle/loading/success/error) → body-swap del modal (AsyncStateView).
  const [addStatus, setAddStatus] = useState('idle');
  const [addErrMsg, setAddErrMsg] = useState('');
  const saving = addStatus === 'loading'; // alias: mantiene los disabled= del form idle.

  // Quitar un supervisor (confirmación).
  const [toRemove, setToRemove] = useState(null); // fila completa

  const abortRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await getSupervisors(controller.signal);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('sup_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchList();
    return () => abortRef.current?.abort();
  }, [fetchList]);

  // Búsqueda dinámica de usuarios (debounce 400ms, mín. 3 caracteres). Reusa
  // getUsersPaged (mismo buscador de usuarios que usa CaseDetails).
  useEffect(() => {
    if (!addModalOpen) return undefined;
    const deb = setTimeout(async () => {
      if (!userSearchText || userSearchText.length < 3) {
        setUserOptions([]);
        return;
      }
      setLoadingUsers(true);
      try {
        const result = await getUsersPaged(1, 20, false, userSearchText);
        const existingIds = new Set(rows.map((r) => r.userId));
        setUserOptions(
          (result?.users || [])
            .filter((u) => !existingIds.has(u.id))
            .map((u) => ({
              label: u.email ? `${u.name} (${u.email})` : u.name,
              value: u.id,
            }))
        );
      } catch {
        setUserOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    }, 400);
    return () => clearTimeout(deb);
  }, [userSearchText, addModalOpen, rows]);

  const openAddModal = useCallback(() => {
    setSelectedUser(null);
    setUserSearchText('');
    setUserOptions([]);
    setError(null);
    setAddStatus('idle');
    setAddErrMsg('');
    setAddModalOpen(true);
  }, []);

  const handleAdd = useCallback(async () => {
    if (!selectedUser?.value) return; // validación pre-submit: se queda inline (idle).
    setAddStatus('loading');
    setAddErrMsg('');
    setError(null);
    try {
      await addSupervisor(selectedUser.value);
      setAddStatus('success');
    } catch (err) {
      setAddErrMsg(
        err.response?.data?.message || err.message || t('sup_error_add')
      );
      setAddStatus('error');
    }
  }, [selectedUser, t]);

  const handleRemove = useCallback(async () => {
    if (!toRemove) return;
    setError(null);
    try {
      await removeSupervisor(toRemove.id);
      setToRemove(null);
      fetchList();
    } catch (err) {
      setToRemove(null);
      setError(
        err.response?.data?.message || err.message || t('sup_error_remove')
      );
    }
  }, [toRemove, fetchList, t]);

  const statusBadge = useCallback(
    (active) => {
      const label = active ? t('sup_status_active') : t('sup_status_inactive');
      return Badge ? (
        <Badge text={label} color={active ? 'success' : 'default'} size='sm' />
      ) : (
        <span>{label}</span>
      );
    },
    [Badge, t]
  );

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        label: t('sup_col_name'),
        sort: true,
        render: (row) => row.name || '—',
      },
      {
        accessor: 'email',
        label: t('sup_col_email'),
        sort: true,
        render: (row) => row.email || '—',
      },
      {
        accessor: 'active',
        label: t('sup_col_status'),
        sort: true,
        render: (row) => (
          <div className='d-flex align-items-start'>
            {statusBadge(row.active)}
          </div>
        ),
      },
      {
        accessor: 'actions',
        label: t('sup_col_actions'),
        render: (row) =>
          IconButton ? (
            <IconButton
              ariaLabel={t('sup_remove')}
              icon='trash'
              size='sm'
              stroke='var(--error-500)'
              onClick={() => setToRemove(row)}
            />
          ) : null,
      },
    ],
    [t, statusBadge, IconButton]
  );

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('sup_title')}</h2>
          <h4 className='fw-normal'>{t('sup_subtitle')}</h4>
        </div>
        <div className='d-flex gap-2 align-items-center'>
          {Button && (
            <Button
              label={t('sup_add')}
              size='sm'
              variant='primary'
              onClick={openAddModal}
            />
          )}
          {IconButton && (
            <IconButton
              ariaLabel={t('sup_refresh')}
              icon='arrowCounterclockwise'
              size='lg'
              iconFill='var(--primary-500)'
              onClick={fetchList}
              disabled={loading}
            />
          )}
        </div>
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div className='d-flex flex-column gap-2'>
            {Skeleton ? (
              [0, 1, 2].map((i) => (
                <Skeleton key={i} height='2.5rem' width='100%' />
              ))
            ) : (
              <p className='font-light'>{t('sup_loading')}</p>
            )}
          </div>
        ) : (
          <div className='table-container na-eepp-grid-4'>
            {Table ? (
              <Table
                columns={columns}
                data={rows}
                loading={loading}
                paginator
                paginationMode='client'
                page={supPage}
                onPageChange={setSupPage}
                pageSize={supPageSize}
                onPageSizeChange={(s) => {
                  setSupPageSize(s);
                  setSupPage(0);
                }}
                totalItems={rows.length}
                paginatorGroup={[
                  { label: '10', value: 10 },
                  { label: '20', value: 20 },
                  { label: '50', value: 50 },
                  { label: '100', value: 100 },
                ]}
              />
            ) : (
              <p className='font-light'>{loading ? t('sup_loading') : null}</p>
            )}
            {!loading && rows.length === 0 && (
              <div className='text-center p-4'>
                <p className='caption font-light'>{t('sup_empty')}</p>
              </div>
            )}
          </div>
        )}

        {/* Alta de un supervisor (picker de usuarios). */}
        {addModalOpen && Modal && (
          <Modal
            title={t('sup_add_title')}
            show
            onClose={() => {
              if (addStatus === 'loading' || addStatus === 'success') return;
              setAddModalOpen(false);
            }}
            size='md'
            primaryBtnTxt={null}
            secondaryBtnTxt={null}
          >
            <AsyncStateView
              status={addStatus}
              loadingTitle={t('sup_state_loading')}
              successTitle={t('sup_state_success')}
              errorMessage={addErrMsg}
              onBack={() => setAddStatus('idle')}
              onSuccessAutoClose={() => {
                setAddModalOpen(false);
                fetchList();
              }}
            >
              <div className='d-flex flex-column gap-3'>
                <p className='caption font-light mb-0'>{t('sup_add_help')}</p>
                {ComboBox ? (
                  <ComboBox
                    label={t('sup_user_label')}
                    placeholder={t('sup_user_placeholder')}
                    noResultText={t('sup_no_users')}
                    value={selectedUser}
                    onChange={(option) => setSelectedUser(option || null)}
                    onInputChange={(text) => setUserSearchText(text)}
                    options={userOptions}
                    isLoading={loadingUsers}
                    isClearable
                  />
                ) : (
                  <>
                    <label
                      className='form-label fw-semibold'
                      style={{ fontSize: 13 }}
                    >
                      {t('sup_user_label')}
                    </label>
                    <input
                      type='text'
                      className='form-control form-control-sm mb-1'
                      placeholder={t('sup_user_placeholder')}
                      value={userSearchText}
                      onChange={(e) => setUserSearchText(e.target.value)}
                    />
                    {userOptions.length > 0 && (
                      <ul
                        className='list-group mb-2'
                        style={{
                          fontSize: 13,
                          maxHeight: 200,
                          overflowY: 'auto',
                        }}
                      >
                        {userOptions.map((u) => (
                          <li
                            key={u.value}
                            className='list-group-item list-group-item-action py-1'
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedUser(u)}
                          >
                            {u.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
                <div className='d-flex gap-2 justify-content-end'>
                  {Button && (
                    <Button
                      label={t('sup_cancel')}
                      size='sm'
                      variant='secondary'
                      onClick={() => setAddModalOpen(false)}
                      disabled={saving}
                    />
                  )}
                  {Button && (
                    <Button
                      label={saving ? t('sup_adding') : t('sup_add_submit')}
                      size='sm'
                      variant='primary'
                      onClick={handleAdd}
                      disabled={saving || !selectedUser?.value}
                    />
                  )}
                </div>
              </div>
            </AsyncStateView>
          </Modal>
        )}

        {/* Confirmación de quitar. */}
        {toRemove && (
          <ConfirmModal
            Modal={Modal}
            show
            title={t('sup_remove_modal_title')}
            message={t('sup_remove_modal_message')}
            confirmLabel={t('sup_remove')}
            cancelLabel={t('sup_cancel')}
            confirmVariant='danger'
            onConfirm={handleRemove}
            onClose={() => setToRemove(null)}
          />
        )}
      </ContentWrapper>
    </div>
  );
}
